import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAxios } from "auth-sdk";
import { UserDataSchemas } from "api-types";
import type { AxiosInstance, AxiosResponse } from "axios";

import { USER_DATA_API, USER_DATA_VALIDATION_API } from "../../components/constants";

// @todo: why a separate function?
const fetchUserData = ({
  client,
  url,
}: {
  client: AxiosInstance;
  url: string;
}): Promise<AxiosResponse<UserDataSchemas.UserData>> => {
  return client.get(url);
};

export const useUserData = (customerSlug: string) => {
  const client = useAxios();
  const fetchUrl = USER_DATA_API.replace(":customerSlug", customerSlug);
  const queryFn = () => fetchUserData({ client, url: fetchUrl });

  return useQuery({
    queryKey: [fetchUrl],
    queryFn,
    select: ({ data }): UserDataSchemas.UserData => {
      return { ...data };
    },
    refetchOnMount: "always",
  });
};

export const useUserDataValidation = (customerSlug: string) => {
  const client = useAxios();
  const url = USER_DATA_VALIDATION_API.replace(":customerSlug", customerSlug);
  return (data: UserDataSchemas.UserDataUpdate) =>
    client.post<UserDataSchemas.UserDataUpdate, AxiosResponse<UserDataSchemas.UserDataValidationResponse>>(url, data);
};

// Omit onSuccess when running multiple mutations in parallel
export const useUserDataMutation = (customerSlug: string, onSuccess?: () => void) => {
  const client = useAxios();
  const queryClient = useQueryClient();
  const mutationUrl = USER_DATA_API.replace(":customerSlug", customerSlug);

  return useMutation({
    mutationKey: ["submitUserData"],
    mutationFn: (data: UserDataSchemas.UserDataUpdate) => {
      return client
        .post<UserDataSchemas.UserDataUpdate, AxiosResponse<UserDataSchemas.UserData>>(mutationUrl, data)
        .then(({ data }: AxiosResponse<UserDataSchemas.UserData>) => data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [mutationUrl] });
      onSuccess?.();
    },
  });
};
